import { useEffect, useState } from "react";
import { Button, Fade, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
	addInterval,
	addIntervalVariant,
	addTimeInterval,
	changeDataInterval,
	changeDaysInterval,
	changeNameInterval,
	changeTimeInterval,
	deleteInterval,
	deleteIntervalVariant,
	deleteTimeInterval,
	editRobot,
	getRobot,
	selectActiveRobotId,
	selectActiveRobotVersion,
	selectEditingRobot,
	selectRobot,
} from "../../../store/ses/sesRobotSlice";
import { useAccessRight, useTranslate } from "@/hooks";
import { SES } from "../../../constants/accessRights";
import { colorPrimary, colorSecondary } from "../../../constants/colors";
import { RequestStatus, ResponseStatus } from "../../../types/statusTypes";
import { DaysType } from "@/types/ses/sesRobotTypes";
import { AlertDialog, BlockWithError, BlockWithLoading } from "@/ui";
import TimeInterval from "../../TimeInterval/TimeInterval";
import styles from "./RobotIntervals.module.scss";
import { Hint } from "@/HOC";
import { selectSesHints } from "@/store/hintsSlice";

const RobotIntervals = (): JSX.Element => {
	const [intervalName, setIntervalName] = useState<string>(""); // выбранное имя интервала
	const [activeTabIndex, setActiveTabIndex] = useState<number>(0); // выбранный таб
	const [changeFlg, setChangeFlg] = useState<boolean>(false); // флаг, уведомляющий об изменении данных и возможности сохранить эти изменения
	const [showAlertDialogDelInterval, setShowAlertDialogDelInterval] = useState<boolean>(false); // показ диалогового окна при удалении интервала

	const dispatch = useAppDispatch();
	const activeRobotId = useAppSelector(selectActiveRobotId); // store - id активного робота
	const robotInfo = useAppSelector(selectRobot); // store - информация о роботе
	const editingRobot = useAppSelector(selectEditingRobot); // store - статус изменения данных робота
	const activeRobotVersion = useAppSelector(selectActiveRobotVersion); // store - версия активного робота
	const sesHints = useAppSelector(selectSesHints);

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	// следим за id робота
	useEffect(() => {
		setChangeFlg(false); // сбрасываем флаг возможности сохранения
	}, [robotInfo.data?.id]);

	// следим за получением данных робота
	useEffect(() => {
		// если есть данные - вписываем в поле первое имя из списка
		if (robotInfo.data) {
			if (Object.keys(robotInfo.data.timeintervals).length > 0) {
				if (Object.keys(robotInfo.data.timeintervals).includes(intervalName)) setIntervalName(intervalName);
				else {
					setIntervalName(Object.keys(robotInfo.data.timeintervals)[0]);
					setActiveTabIndex(0);
				}
			} else {
				setIntervalName("");
				setActiveTabIndex(0);
			}
		}
	}, [robotInfo.data]);

	// следим за статусом изменения робота
	useEffect(() => {
		// если изменение данных робота прошло успешно
		if (editingRobot.status === RequestStatus.IDLE && editingRobot.error === ResponseStatus.SUCCESS && editingRobot.message !== "") {
			setChangeFlg(false); // сбрасываем флаг о несохраненных данных
		}
	}, [editingRobot]);

	// обработчик сохранения данных робота
	const editRobotHandler = (): void => {
		robotInfo.data &&
			dispatch(
				editRobot({
					robotId: robotInfo.data.id,
					data: {
						timeintervals: robotInfo.data.timeintervals,
					},
				})
			); // изменяем данные робота
	};

	// проверка на права
	const checkAccesToChangeTimeInterval = (changeFn: () => void) => {
		if (isAccess(SES.ROBOT_EDIT) && activeRobotVersion === "draft") {
			changeFn();
			setChangeFlg(true); // ставим флаг о несохраненных данных
		}
	};

	// обработчик добавления интервала
	const addIntervalHandler = (): void => {
		checkAccesToChangeTimeInterval(() => {
			if (robotInfo.data) {
				const newName = `${translate("title_newInterval")} ${Object.keys(robotInfo.data.timeintervals).length + 1}`;
				dispatch(addInterval(newName)); // добавляем новый интервал
				setIntervalName(newName); // выбираем его
				setActiveTabIndex(0);
			}
		});
	};

	// обработчик удаления интервала
	const deleteIntervalHandler = (): void => {
		checkAccesToChangeTimeInterval(() => {
			dispatch(deleteInterval(intervalName)); // удаляем интервал
			setIntervalName(""); // сбрасываем выбор интервала
			setActiveTabIndex(0); // сбрасываем таб
		});
	};

	// обработчик изменения названия
	const handleChangeName = (name: string): void => {
		checkAccesToChangeTimeInterval(() => {
			if (intervalName !== name) {
				dispatch(changeNameInterval({ oldName: intervalName, newName: name })); // меняем старое на новое название
				setIntervalName(name); // смена имени в выборе интервала
			}
		});
	};

	// обработчик добавления варианта интервла
	const addIntervalVariantHandler = (): void => {
		checkAccesToChangeTimeInterval(() => {
			if (robotInfo.data) {
				dispatch(addIntervalVariant(intervalName));
				setActiveTabIndex(robotInfo.data.timeintervals[intervalName].length);
			}
		});
	};

	// обработчик удаления варианта интервла
	const deleteIntervalVariantHandler = (): void => {
		checkAccesToChangeTimeInterval(() => {
			if (robotInfo.data?.timeintervals[intervalName].length === 1) {
				deleteIntervalHandler();
			} else {
				dispatch(deleteIntervalVariant({ intervalName, intervalIdx: activeTabIndex }));
				setActiveTabIndex(0);
			}
		});
	};

	// обработчик изменения дней по производственному календарю
	const handleChangeDays = (e: SelectChangeEvent<DaysType>): void => {
		checkAccesToChangeTimeInterval(() => {
			dispatch(changeDaysInterval({ intervalName, intervalIdx: activeTabIndex, days: e.target.value as DaysType }));
		});
	};

	// обработчик изменения дней недели
	const handleChangeWeekDays = (weekdays: number[], day: number): void => {
		checkAccesToChangeTimeInterval(() => {
			dispatch(
				changeDataInterval({
					intervalName,
					intervalIdx: activeTabIndex,
					data: weekdays.includes(day) ? weekdays.filter((weekday) => weekday !== day) : [...weekdays, day],
					objectOfChange: "weekdays",
				})
			);
		});
	};

	// обработчик изменения месяцев
	const handleChangeMonths = (months: number[], month: number): void => {
		checkAccesToChangeTimeInterval(() => {
			dispatch(
				changeDataInterval({
					intervalName,
					intervalIdx: activeTabIndex,
					data: months.includes(month) ? months.filter((monthNum) => monthNum !== month) : [...months, month],
					objectOfChange: "months",
				})
			);
		});
	};

	// обработчик изменения дней месяца
	const handleChangeDate = (dates: number[], date: number): void => {
		checkAccesToChangeTimeInterval(() => {
			dispatch(
				changeDataInterval({
					intervalName,
					intervalIdx: activeTabIndex,
					data: dates.includes(date) ? dates.filter((dateNum) => dateNum !== date) : [...dates, date],
					objectOfChange: "dates",
				})
			);
		});
	};

	// обработчик добавления времени интервала
	const handleAddTimePeriod = (): void => {
		checkAccesToChangeTimeInterval(() => {
			dispatch(addTimeInterval({ intervalName, intervalIdx: activeTabIndex }));
		});
	};

	// обработчик изменения времени интервала
	const handleChangeTimeInterval = (timeInterval: [number, number], idx?: number) => {
		checkAccesToChangeTimeInterval(() => {
			if (idx !== undefined) {
				dispatch(
					changeTimeInterval({
						intervalName,
						intervalIdx: activeTabIndex,
						data: timeInterval,
						idx,
					})
				);
			}
		});
	};

	// обработчик удаления времени
	const handleDeleteTime = (idx: number): void => {
		checkAccesToChangeTimeInterval(() => {
			dispatch(deleteTimeInterval({ intervalName, intervalIdx: activeTabIndex, idx }));
		});
	};

	return (
		<div className={styles.configRobot}>
			{/* загрузка настроек робота */}
			{robotInfo.status === RequestStatus.LOADING && <BlockWithLoading />}

			{/* ошибка получения настроек робота */}
			{robotInfo.status === RequestStatus.FAILED && (
				<BlockWithError
					message={robotInfo.message}
					updateHandler={() => {
						isAccess(SES.ROBOT_GET) && activeRobotId && dispatch(getRobot(activeRobotId)); // получаем инфо о роботе;
					}}
				/>
			)}

			{/* настройки интервалов */}
			{robotInfo.status === RequestStatus.IDLE && robotInfo.data && (
				<Fade in={true} timeout={500}>
					<Hint hint={sesHints.hint_sesBlockTimeInterval} position="left">
						<div className={styles.configRobotWrapper}>
							<div className={styles.configRobotTop}>
								{/* выбор интервала */}
								<FormControl fullWidth margin="dense" sx={{ '.MuiInputLabel-root[data-shrink="false"]': { top: -8 }, ".MuiSelect-select": { paddingBlock: 0 } }}>
									<>
										<InputLabel sx={{ fontSize: 13 }}>{translate("select_timeInterval")}</InputLabel>
										<Select
											required
											label={translate("select_timeInterval")}
											value={intervalName}
											onChange={(e) => {
												if (e.target.value) {
													setIntervalName(e.target.value);
													setActiveTabIndex(0);
												}
											}}
											style={{ fontSize: 13, height: 33, color: colorPrimary, width: "100%" }}
										>
											{isAccess(SES.ROBOT_EDIT) && activeRobotVersion === "draft" && (
												<MenuItem sx={{ fontSize: 13, color: colorSecondary }} onClick={addIntervalHandler}>
													{translate("selectItem_addTimeInterval")}
												</MenuItem>
											)}
											{Object.keys(robotInfo.data.timeintervals).map((name) => (
												<MenuItem key={name} value={name} sx={{ fontSize: 13, color: colorPrimary }}>
													{name}
												</MenuItem>
											))}
										</Select>
									</>
								</FormControl>

								{/* временной интервал */}
								{Object.keys(robotInfo.data.timeintervals).includes(intervalName) ? (
									<TimeInterval
										timeinterval={robotInfo.data.timeintervals[intervalName][activeTabIndex]}
										intervalName={intervalName}
										setIntervalName={setIntervalName}
										setChangeFlg={setChangeFlg}
										changeNameHandler={handleChangeName}
										changeDaysHandler={handleChangeDays}
										changeWeekDaysHandler={handleChangeWeekDays}
										changeMonthsHandler={handleChangeMonths}
										changeDateHandler={handleChangeDate}
										addTimePeriodHandler={handleAddTimePeriod}
										changeTimeIntervalHandler={handleChangeTimeInterval}
										deleteTimeHandler={handleDeleteTime}
										isDisabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
										tabbedDisplayMode={{
											arrayLength: robotInfo.data.timeintervals[intervalName].length,
											activeTabIndex,
											setActiveTabIndex,
											addIntervalVariantHandler,
											deleteIntervalVariantHandler,
										}}
									/>
								) : (
									<div className={styles.configRobotTopNotFoundTitle}>{translate("title_notFound")}</div>
								)}
							</div>

							{activeRobotVersion === "draft" && isAccess(SES.ROBOT_EDIT) && (
								<div className={styles.configRobotBottom}>
									<FormControl fullWidth>
										<Button fullWidth variant="outlined" disabled={!changeFlg} sx={{ fontSize: 11 }} onClick={editRobotHandler}>
											{translate("button_save")}
										</Button>
									</FormControl>
									<FormControl fullWidth>
										<Button
											fullWidth
											variant="outlined"
											sx={{ fontSize: 11 }}
											disabled={!Object.keys(robotInfo.data.timeintervals).includes(intervalName)}
											onClick={() => setShowAlertDialogDelInterval(true)}
											color="error"
										>
											{translate("button_deleteGroup")}
										</Button>
									</FormControl>
								</div>
							)}
						</div>
					</Hint>
				</Fade>
			)}

			{/* удаление группы интервалов */}
			<AlertDialog
				showAlertDialog={showAlertDialogDelInterval}
				setShowAlertDialog={setShowAlertDialogDelInterval}
				submitHandler={deleteIntervalHandler}
				title="dialog_deleteGroupIntervals"
				description="dialog_deleteGroupIntervalsConfirm"
				name={intervalName}
			/>
		</div>
	);
};

export default RobotIntervals;
